import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, hhmmss, MSG_TYPES, msgTypeLabel, shortStamp, statusKind, statusLabel, type ApiSupplierDetail, type ApiSupplierRow, type FetchNowResult } from '../api';
import { Icon } from '../components/icons';
import { mailboxDraft, MailboxFields, mailboxInput, mailboxMissing } from '../components/MailboxFields';
import { protoLabel, ProtoTiles, type FileProto, type Proto } from '../components/ProtoTiles';
import { Modal } from '../components/Modal';
import { ServerIdentityBlock } from '../components/ServerIdentity';
import { Badge, Btn, Card, DataGate, Dot, PageHead, Spinner } from '../components/ui';
import { configIssueShort } from '../config-issue';
import { useApp } from '../state';
import { useResource, useFreshSignal } from '../useResource';

// Fournisseurs — la source des ÉCHANGES : chaque fournisseur porte son transport (config chiffrée §13), ses
// mappings et ses liens. Fidèle à la maquette : un TABLEAU (identité + transport + compteurs) surmonte une
// FICHE à 4 onglets (Transports/Mappings/Clients/Activité) ; créer/modifier passe par un WIZARD en 3 étapes
// (Identité+Transport → Types → Récapitulatif). Tout est réel et honnête (§23.0).

/** Le nombre de boîtes client d'un fournisseur par e-mail, accordé. */
const mailboxCount = (n: number): string => (n === 0 ? 'aucune boîte client' : n === 1 ? '1 boîte client' : `${n} boîtes client`);
type SupTab = 'transports' | 'mappings' | 'links' | 'activity';

const inp = { height: 36, borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', padding: '0 11px', width: '100%' } as const;
const lbl = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 5, display: 'block' } as const;
const th = { textAlign: 'left', padding: '0 10px', height: 34, fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--ink-200)' } as const;

type Toast = (m: string, t?: 'ok' | 'warn' | 'err' | 'info') => void;

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '—';
}

export function Fournisseurs() {
  const { showToast, guard } = useApp();
  const nav = useNavigate();
  const [selId, setSelId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApiSupplierDetail | null>(null);
  const [detailFailed, setDetailFailed] = useState(false); // échec de chargement de la fiche → message INLINE (pas de disparition silencieuse)
  const [tab, setTab] = useState<SupTab>('transports');
  const [wizard, setWizard] = useState<'create' | 'edit' | null>(null); // wizard (modale) crée/modifie
  const [txAction, setTxAction] = useState<{ action: TxAction; tx: ApiSupplierDetail['transports'][number] } | null>(null); // modale test/relève
  const [mailboxModal, setMailboxModal] = useState<{ linkId: string | null } | null>(null); // boîte d'un client (e-mail, une par magasin)

  // Aucun fournisseur = un vide RÉEL (base neuve) : l'écran le DIT au lieu d'afficher un tableau creux.
  const { state, data, reload, error } = useResource(
    (signal) => api.get<ApiSupplierRow[]>('/admin/suppliers', signal),
    (list) => list.length === 0,
  );
  // ⚠️ REPLI MÉMOÏSÉ et non `data ?? []` — la même leçon que le journal, et la porte `lint` l'a redite : une
  //    identité neuve à chaque rendu ferait recalculer le tri à chaque rendu, donc le mémo ne mémoriserait rien.
  const liste = useMemo(() => data ?? [], [data]);

  /**
   * TRI PAR COLONNE — relevé de la maquette, où chaque en-tête est un bouton avec sa flèche.
   *
   * ⚠️ IL EST ENTIÈREMENT CÔTÉ ÉCRAN, et c'est légitime ici : la liste est courte (un fournisseur par
   * plateforme partenaire) et elle est déjà chargée en entier. Un tri serveur demanderait un paramètre de
   * route pour un gain nul — alors qu'un tri qui ne porte que sur la PAGE affichée serait, lui, un
   * mensonge (il prétendrait classer un ensemble qu'il ne voit pas).
   */
  const [sortKey, setSortKey] = useState<'name' | 'transport' | 'active' | 'sitesCount' | 'mappingsCount' | 'errorsCount'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const rows = useMemo(() => {
    const val = (s: ApiSupplierRow): string | number => {
      switch (sortKey) {
        case 'transport': return s.transport?.type ?? ''; // « aucun transport » se classe en tête, et c'est voulu
        case 'active': return s.active ? 1 : 0;
        case 'sitesCount': return s.sitesCount;
        case 'mappingsCount': return s.mappingsCount;
        case 'errorsCount': return s.errorsCount;
        default: return s.name.toLowerCase();
      }
    };
    return [...liste].sort((a, b) => {
      const av = val(a); const bv = val(b);
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? c : -c;
    });
  }, [liste, sortKey, sortDir]);

  /** En-tête cliquable : même geste que le journal (re-cliquer inverse le sens). */
  const thSort = (key: typeof sortKey, label: string, align: 'left' | 'right' = 'left') => (
    <th style={{ ...th, textAlign: align }}>
      <button type="button" onClick={() => { if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setSortKey(key); setSortDir('asc'); } }}
        style={{ all: 'unset', cursor: 'pointer', font: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', whiteSpace: 'nowrap' }}>
        {label}{sortKey === key && <span style={{ marginLeft: 4, color: 'var(--teal-600)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );

  // ⚠️ La présélection est un effet du chargement RÉUSSI, pas du chargement lui-même : posée ici, elle ne
  // peut plus ouvrir la fiche d'un fournisseur issu d'une réponse abandonnée. `cur ??` préserve le choix
  // de l'opérateur à travers les rechargements (une relève ne doit pas lui reprendre sa sélection).
  useEffect(() => { if (data) setSelId((cur) => cur ?? data[0]?.id ?? null); }, [data]);

  // ⚠️ Le signal périme le chargement précédent : sans lui, enchaîner deux fournisseurs affichait les
  // liens de l'un sous le nom de l'autre, sans que rien ne le signale (cf. `useFreshSignal`).
  const signalNeuf = useFreshSignal();
  const loadDetail = useCallback(async (id: string) => {
    const signal = signalNeuf();
    setDetail(null);
    setDetailFailed(false);
    try {
      const record = await api.get<ApiSupplierDetail>(`/admin/suppliers/${id}`, signal);
      if (signal.aborted) return;
      setDetail(record);
    } catch {
      if (signal.aborted) return; // un autre fournisseur a été demandé : ce n'est pas un échec
      setDetailFailed(true); // message inline sous le tableau (pas un simple toast qui disparaît)
    }
  }, [signalNeuf]);
  useEffect(() => { if (selId) void loadDetail(selId); }, [selId, loadDetail]);

  const togglePause = async () => {
    if (!detail || !guard()) return;
    try {
      await api.patch(`/admin/suppliers/${detail.supplier.id}`, { active: !detail.supplier.active });
      showToast(detail.supplier.active ? 'Fournisseur mis en pause' : 'Fournisseur réactivé', 'ok');
      await loadDetail(detail.supplier.id);
      await reload();
    } catch (e) {
      showToast('Échec : ' + (e as Error).message, 'err');
    }
  };

  const afterWizard = async (id?: string) => {
    setWizard(null);
    await reload();
    if (id) { setSelId(id); await loadDetail(id); }
    else if (selId) await loadDetail(selId);
  };

  /** Recharge tableau + fiche (après une action qui change les données : relève, pause…). */
  const refreshBoth = async () => {
    await reload();
    if (selId) await loadDetail(selId);
  };

  const pageHead = (
    <PageHead title="Fournisseurs" sub={`${rows.length} plateforme(s) émettrice(s)/réceptrice(s) · transport configuré une fois, valable pour tous les clients liés`}
      actions={<Btn icon="plus" onClick={() => { if (guard()) setWizard('create'); }}>Nouveau fournisseur</Btn>} />
  );

  return (
    <>
    <DataGate state={state} onRetry={reload} empty={{ title: 'Aucun fournisseur', text: 'Créez un premier fournisseur : identité, transport, puis les types de messages qu\'il échange.' }} error={error} head={pageHead}>

      {/* TABLEAU — clic sur une ligne = sélection (ouvre la fiche dessous). */}
      <Card>
        <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, flex: 1, color: 'var(--ink-800)' }}>{rows.length === 1 ? 'Le fournisseur' : `Les ${rows.length} fournisseurs`}</span>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>Cliquez une ligne pour ouvrir sa fiche</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse', fontSize: 'var(--t-body)' }}>
            <thead>
              {/* ⚠️ COLONNE « CLIENTS » AJOUTÉE (relevé de la maquette) : `sitesCount` était DÉJÀ servi et
                  déjà affiché, mais noyé dans le sous-titre de la première colonne — donc impossible à
                  comparer d'une ligne à l'autre, et impossible à trier. C'était un manque d'ÉCRAN, pas de
                  données : rien à demander au serveur. */}
              <tr>
                {thSort('name', 'Fournisseur')}
                {thSort('transport', 'Transport')}
                {thSort('active', 'Statut')}
                {thSort('sitesCount', 'Clients', 'right')}
                {thSort('mappingsCount', 'Mappings', 'right')}
                {thSort('errorsCount', 'Erreurs', 'right')}
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} onClick={() => { setSelId(s.id); setTab('transports'); }}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--ink-100)', background: s.id === selId ? 'var(--ink-50)' : 'transparent' }}>
                  <td style={{ padding: '9px 12px 9px 16px' }}>
                    {/* ⚠️ LE NOM EST LE BOUTON DE LA LIGNE — le chemin du clavier ; le clic sur la ligne n'en est qu'un raccourci. */}
                    <button type="button" aria-current={s.id === selId || undefined}
                      onClick={(e) => { e.stopPropagation(); setSelId(s.id); setTab('transports'); }}
                      style={{ display: 'block', padding: 0, border: 'none', background: 'none', font: 'inherit', textAlign: 'left', cursor: 'pointer', fontWeight: 600, color: 'var(--ink-800)', borderRadius: 'var(--r-xs)' }}>
                      {s.name}
                    </button>
                    {/* Le compte de clients a sa COLONNE : le répéter ici ferait lire deux fois le même
                        chiffre, et la maquette ne garde que le GLN sous le nom. */}
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
                      {s.gln ? `GLN ${s.gln}` : 'GLN —'}
                    </div>
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    {s.protocol === 'email' ? (
                      <>
                        <div style={{ fontWeight: 600, fontSize: 'var(--t-note)', color: 'var(--ink-700)' }}>E-mail</div>
                        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{mailboxCount(s.transportCount)}</div>
                        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>dernier · {shortStamp(s.lastExchangeAt)}</div>
                      </>
                    ) : s.transport ? (
                      <>
                        <div style={{ fontWeight: 600, textTransform: 'uppercase', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-700)' }}>{s.transport.type}</div>
                        <div title={s.transport.configIssue?.detail} style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{s.transport.host ?? configIssueShort(s.transport.configIssue) ?? '—'}</div>
                        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>dernier · {shortStamp(s.lastExchangeAt)}</div>
                      </>
                    ) : <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{s.protocol ? `${protoLabel(s.protocol)} · aucun transport` : 'aucun transport'}</span>}
                  </td>
                  <td style={{ padding: '9px 12px' }}><Badge kind={s.active ? 'ok' : 'off'}>{s.active ? 'Actif' : 'En pause'}</Badge></td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: s.sitesCount > 0 ? 'var(--ink-700)' : 'var(--ink-400)' }}>{s.sitesCount}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-700)' }}>{s.mappingsCount}</td>
                  <td style={{ padding: '9px 14px 9px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: s.errorsCount > 0 ? 700 : 400, color: s.errorsCount > 0 ? 'var(--rose-fg)' : 'var(--ink-400)' }}>{s.errorsCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* FICHE — en-tête + onglets, sur le fournisseur sélectionné. Échec de chargement = message INLINE. */}
      {detailFailed && (
        <Card style={{ marginTop: 14, padding: 16 }}>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="alert" size={15} />Fiche fournisseur indisponible (le service n'a pas répondu).
            <Btn size="sm" variant="ghost" mutating={false} icon="refresh" onClick={() => { if (selId) void loadDetail(selId); }}>Réessayer</Btn>
          </div>
        </Card>
      )}
      {detail && (
        <Card style={{ marginTop: 14 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-dense)', fontWeight: 700, flexShrink: 0 }}>{initials(detail.supplier.name)}</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{detail.supplier.name}</div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{detail.supplier.gln ? `GLN ${detail.supplier.gln}` : 'GLN non déclaré'} · {detail.supplier.protocol === 'email' ? `E-mail · ${mailboxCount(detail.transports.length)}` : detail.supplier.protocol ? protoLabel(detail.supplier.protocol) : 'protocole non choisi'} · {detail.mappings.length} mapping(s)</div>
            </div>
            <Badge kind={detail.supplier.active ? 'ok' : 'off'}>{detail.supplier.active ? 'Actif' : 'En pause'}</Badge>
            <Btn size="sm" variant="ghost" mutating={false} icon="cog" onClick={() => { if (guard()) setWizard('edit'); }}>Modifier</Btn>
            <Btn size="sm" variant={detail.supplier.active ? 'ghost' : 'primary'} icon={detail.supplier.active ? 'pause' : 'play'} onClick={togglePause}>{detail.supplier.active ? 'Mettre en pause' : 'Reprendre'}</Btn>
          </div>

          <div style={{ display: 'flex', gap: 2, padding: '9px 16px 0', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
            <TabBtn on={tab === 'transports'} onClick={() => setTab('transports')}>Transports</TabBtn>
            <TabBtn on={tab === 'mappings'} onClick={() => setTab('mappings')}>Mappings ({detail.mappings.length})</TabBtn>
            <TabBtn on={tab === 'links'} onClick={() => setTab('links')}>Clients ({detail.links.length})</TabBtn>
            <TabBtn on={tab === 'activity'} onClick={() => setTab('activity')}>Activité</TabBtn>
          </div>

          <div style={{ padding: '14px 16px' }}>
            {tab === 'transports' && (() => {
              // Le protocole se LIT d'abord (tuiles de la maquette, celle du fournisseur surlignée) : un fournisseur par
              // e-mail n'a aucun serveur à montrer, ses boîtes sont celles de ses clients.
              const isEmail = detail.supplier.protocol === 'email';
              const emailAccounts = detail.transports.filter((t) => t.type === 'email');
              const fileTransports = detail.transports.filter((t) => t.type !== 'email');
              const linkOf = (g: string | null) => (g ? detail.links.find((l) => l.gln === g) ?? null : null);
              const withoutMailbox = detail.links.filter((l) => !emailAccounts.some((t) => t.gln === l.gln));
              return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {!detail.supplier.active && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
                    <Icon name="pause" size={15} /><span><b style={{ fontWeight: 600 }}>Fournisseur en pause.</b> Aucune récupération ni dépôt ; les sorties de ses clients s'empilent en file d'attente.</span>
                  </div>
                )}
                <ProtoTiles value={detail.supplier.protocol} />

                {isEmail ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
                      <Icon name="info" size={15} /><span>Ce fournisseur échange <b style={{ fontWeight: 600 }}>par e-mail</b> : il reçoit nos envois à son adresse EDI et nous écrit depuis ses expéditeurs, les mêmes pour tous ses clients (<b style={{ fontWeight: 600 }}>Modifier</b>). Chaque client relève <b style={{ fontWeight: 600 }}>sa propre boîte</b>, une par magasin, déclarée sur son lien.</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      <ReadField label="Adresse EDI du fournisseur" value={detail.supplier.emailAddress ?? 'non déclarée'} mono />
                      <ReadField label="Expéditeurs autorisés" value={detail.supplier.emailSenders.length ? detail.supplier.emailSenders.join(', ') : 'aucun — tout mail est refusé'} mono />
                    </div>
                    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                        <Icon name="mail" size={16} />
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>Boîtes des clients <span style={{ fontWeight: 400, color: 'var(--ink-500)' }}>· une par magasin, même mapping fournisseur</span></div>
                          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.45 }}>Chaque client relève sa boîte et envoie depuis sa propre adresse, chez l’opérateur de son choix.</div>
                        </div>
                        {withoutMailbox.length > 0 && <Btn size="sm" icon="plus" mutating={false} onClick={() => { if (guard()) setMailboxModal({ linkId: null }); }}>Déclarer une boîte</Btn>}
                      </div>
                      {emailAccounts.length === 0 && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Aucune boîte client.{detail.links.length === 0 && ' Liez d’abord un client (écran Clients) : sa boîte se déclare avec le lien.'}</div>}
                      {emailAccounts.map((tr) => {
                        const link = linkOf(tr.gln);
                        return (
                          <EmailAccountCard key={tr.id} tr={tr} clientName={link?.name ?? null}
                            onTest={() => setTxAction({ action: 'test', tx: tr })}
                            onFetch={() => setTxAction({ action: 'fetch', tx: tr })}
                            onEdit={link ? () => { if (guard()) setMailboxModal({ linkId: link.id }); } : null} />
                        );
                      })}
                      {withoutMailbox.length > 0 && (
                        <div role="status" style={{ fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <Icon name="alert" size={13} /><span>Sans boîte, rien n’est relevé ni envoyé pour {withoutMailbox.map((l) => l.name ?? l.gln).join(', ')}.</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Transport FICHIER — un seul, configuré via Modifier. Secret jamais réaffiché (§13). */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
                      <Icon name="info" size={15} /><span>Le serveur de fichiers se configure via <b style={{ fontWeight: 600 }}>Modifier</b> et vaut pour tous les clients liés. Les identifiants sont chiffrés au repos (§13) et jamais réaffichés.</span>
                    </div>
                    {fileTransports.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun serveur enregistré — utilisez <b>Modifier</b> pour le configurer.</div>}
                    {fileTransports.map((tr) => (
                      <div key={tr.id} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Dot kind={tr.active ? 'ok' : 'off'} />
                          <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, textTransform: 'uppercase', fontFamily: 'var(--font-mono)', color: 'var(--ink-800)' }}>{tr.type}</span>
                          {!tr.active && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>· inactif</span>}
                          <span style={{ flex: 1 }} />
                          <Btn size="sm" variant="ghost" mutating={false} icon="play" onClick={() => setTxAction({ action: 'test', tx: tr })}>Tester la connexion</Btn>
                          <Btn size="sm" variant="ghost" mutating={false} icon="download" onClick={() => setTxAction({ action: 'fetch', tx: tr })}>Relever maintenant</Btn>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                          <ReadField label="Hôte" value={tr.config?.host ?? '—'} mono />
                          <ReadField label="Utilisateur" value={tr.config?.user ?? '—'} mono />
                          <ReadField label="Secret" value={tr.configIssue?.kind === 'no_secret' ? 'aucun' : '•••••••• · chiffré'} muted mono />
                        </div>
                        {/* La cause réelle d'une configuration absente (§23.0) : un « — » ne dirait pas quel geste la rétablit. */}
                        {tr.configIssue && <div role="status" style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{tr.configIssue.detail}</div>}
                      </div>
                    ))}
                  </>
                )}
              </div>
              );
            })()}

            {tab === 'mappings' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {detail.mappings.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun mapping — le fournisseur ne peut pas encore être traduit.</div>}
                {detail.mappings.map((mp) => (
                  <div key={mp.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', flexWrap: 'wrap' }}>
                    <Dot kind="ok" />
                    <div style={{ minWidth: 160, flex: 1 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-800)' }}>{mp.canonicalType}</span>
                      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-700)', marginLeft: 8 }}>{mp.name}</span>
                      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{mp.sourceFormat} « {mp.discriminant} » · v{mp.currentVersion} en service</div>
                    </div>
                  </div>
                ))}
                {/* ⚠️ Un mapping peut servir à plusieurs fournisseurs : ses versions et ses rattachements se gèrent
                    à un seul endroit, l'écran Mappings, pour qu'une correction ne s'y fasse jamais fournisseur par fournisseur. */}
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>Rattachements, versions et retour arrière dans l'écran <LinkBtn onClick={() => nav('/mappings')}>Mappings</LinkBtn> — un mapping peut y servir à plusieurs fournisseurs.</div>
              </div>
            )}

            {tab === 'links' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {detail.links.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun client lié.</div>}
                {detail.links.map((lk) => (
                  <div key={lk.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{lk.name ?? 'Site inconnu'}</div>
                      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{lk.gln}</div>
                    </div>
                    <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>{lk.messageTypes.map(msgTypeLabel).join(' · ') || 'aucun type'}</span>
                    <Btn size="sm" variant="ghost" mutating={false} onClick={() => nav('/clients')}>Ouvrir le client</Btn>
                  </div>
                ))}
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>Un lien s'ajoute depuis la fiche du client — <LinkBtn onClick={() => nav('/clients')}>ouvrir Clients</LinkBtn>.</div>
              </div>
            )}

            {tab === 'activity' && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {detail.recent.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun échange pour ce fournisseur.</div>}
                {detail.recent.map((m) => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 2px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{hhmmss(m.createdAt)}</span>
                    <span style={{ display: 'flex', color: m.direction === 'in' ? 'var(--ink-700)' : 'var(--ink-500)' }}><Icon name={m.direction === 'in' ? 'inbound' : 'outbound'} size={13} sw={2} /></span>
                    <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-700)' }}>{m.canonicalType ?? '—'}</span>
                    <Badge kind={statusKind(m.status)}>{statusLabel(m.status)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </DataGate>
    {wizard && (
      <SupplierWizard
        mode={wizard}
        supplier={wizard === 'edit' ? detail : null}
        onClose={() => setWizard(null)}
        onDone={afterWizard}
        showToast={showToast}
      />
    )}
    {txAction && detail && (
      <TransportActionModal action={txAction.action} transport={txAction.tx} supplierName={detail.supplier.name}
        onClose={() => setTxAction(null)} onDone={refreshBoth} />
    )}
    {mailboxModal && detail && (
      <MailboxModal supplier={detail} linkId={mailboxModal.linkId}
        onClose={() => setMailboxModal(null)} onDone={async () => { setMailboxModal(null); await refreshBoth(); }} showToast={showToast} />
    )}
    </>
  );
}

// Modale d'ACTION sur un transport — reprise EXACTE de la maquette (§ Fournisseurs / PVH) : phases
// confirmation → en cours → résultat, l'en-tête (icône teintée + titre + sous-titre) DEVIENT le verdict, et
// le détail s'affiche dans un bloc `<pre>` bordé (rose si échec). Deux actions, un seul composant :
//   • `test`  — sonde la connexion (aucun fichier touché) ;
//   • `fetch` — RELÈVE MAINTENANT : exécute le vrai tunnel de réception (les fichiers présents sont
//               récupérés, traduits et journalisés) → compteurs réels. §23.0 : verdicts jamais simulés.
type TxAction = 'test' | 'fetch';
function TransportActionModal({ action, transport, supplierName, onClose, onDone }: {
  action: TxAction;
  transport: ApiSupplierDetail['transports'][number];
  supplierName: string;
  onClose: () => void;
  onDone: () => void | Promise<void>; // rafraîchit la fiche : une relève crée de vrais messages
}) {
  const { guard } = useApp(); // actions serveur réelles → gardées comme les autres mutations
  const [phase, setPhase] = useState<'confirm' | 'running' | 'done'>('confirm');
  const [result, setResult] = useState<FetchNowResult | null>(null);
  const [ms, setMs] = useState<number | null>(null);
  const host = transport.config?.host ?? '';
  const proto = transport.type.toUpperCase();
  const isFetch = action === 'fetch';

  const run = async () => {
    if (!guard()) return;
    setPhase('running');
    const t0 = performance.now();
    let r: FetchNowResult;
    try {
      r = isFetch ? await api.fetchNow(transport.id) : await api.testTransport(transport.id);
    } catch (e) {
      r = { ok: false, error: (e as Error).message };
    }
    setMs(Math.round(performance.now() - t0));
    setResult(r);
    setPhase('done');
    if (isFetch && r.ok) await onDone(); // le journal/les compteurs ont changé
  };

  const ok = result?.ok ?? false;
  // ⚠️ 🔴 UNE RELÈVE PEUT RÉUSSIR *ET* AVOIR SAUTÉ UN CLIENT. Depuis qu'un dossier introuvable n'interrompt
  //    plus la collecte des autres, `ok: true` ne veut plus dire « tout a été relevé » — annoncer « Relève
  //    terminée » sur un fournisseur dont un client n'a même pas pu être listé serait exactement le succès
  //    rassurant que le §23.0 interdit. Le titre le DIT, et le détail nomme les clients concernés.
  const sautes = result?.unreachable ?? [];
  // En-tête piloté par la phase (comme la maquette : le « done » RÉUTILISE l'en-tête pour le verdict).
  const title = phase !== 'done'
    ? (isFetch ? 'Relever maintenant' : 'Test de connexion')
    : ok
      ? (isFetch ? (sautes.length ? 'Relève incomplète' : 'Relève terminée') : 'Connexion établie')
      : (isFetch ? 'Relève impossible' : 'Connexion refusée');
  const sub = phase === 'done'
    ? (ok ? `terminé en ${ms} ms` : `échec après ${ms} ms`)
    : `${supplierName} · ${proto}${host ? ' ' + host : ''}`;
  const tone = phase === 'done' ? (ok ? 'ok' : 'err') : 'ink';
  const icon = phase === 'done' ? (ok ? 'check' : 'x') : isFetch ? 'download' : 'play';

  // Résultat d'une relève : les compteurs RÉELS du tunnel (0 fichier est un résultat, pas un échec).
  const fetchSummary = (r: FetchNowResult): string => {
    const entete = `${proto}${host ? ' ' + host : ''}`;
    // ⚠️ 🔴 LES DOSSIERS SAUTÉS PASSENT AVANT TOUT LE RESTE : ils nomment le client dont le dossier n'existe
    //    pas sur le serveur du fournisseur, avec le chemin exact et le message réel. C'est l'information
    //    qui manquait à l'incident — « 550 Directory not found. » nu ne désignait personne.
    const injoignables = (r.unreachable ?? []).map((u) => `⚠ ${u.label} — ${u.message}\n   dossier attendu : ${u.dir}`);
    // Les mails REFUSÉS (expéditeur non autorisé) : nommés par leur objet, marqués lus, jamais relevés.
    const refuses = (r.refused ?? []).map((x) => `⛔ « ${x.name} » — ${x.message}`);
    // ⚠️ « Aucun fichier » ne vaut que si RIEN n'a été trouvé. Des fichiers présents mais illisibles laissent
    // `fetched` à 0 alors qu'il y avait bien quelque chose : les annoncer « aucun » serait faux (§23.0).
    // ⚠️ ET UN DOSSIER INJOIGNABLE INTERDIT CETTE PHRASE : on n'a pas pu REGARDER, donc on ne peut pas dire
    //    qu'il n'y avait rien. C'est la même faute, un cran plus haut.
    if ((r.fetched ?? 0) === 0 && (r.unfetched ?? 0) === 0 && injoignables.length === 0 && refuses.length === 0) {
      return `${entete}\nAucun fichier à relever dans les dossiers entrants.`;
    }
    if ((r.fetched ?? 0) === 0 && (r.unfetched ?? 0) === 0) {
      return [entete, ...injoignables, ...refuses, injoignables.length ? 'Aucun fichier dans les dossiers qui ont pu être lus. Les dossiers ci-dessus n’ont PAS été regardés.' : 'Aucun fichier relevé : les mails ci-dessus viennent d’expéditeurs non autorisés (marqués lus, restés dans la boîte).'].join('\n');
    }
    return [
      entete,
      ...injoignables,
      ...refuses,
      `${r.fetched} fichier(s) relevé(s)${(r.unfetched ?? 0) > 0 ? ` · ${r.unfetched} NON relevé(s)` : ''}`,
      `${r.translated} traduit(s) · ${r.unrecognized} non reconnu(s) · ${r.misrouted ?? 0} mauvais magasin · ${r.duplicate ?? 0} déjà intégré(s) · ${r.errors} en erreur`,
      (r.unrecognized ?? 0) > 0 ? 'Les non reconnus sont CONSERVÉS : ajoutez le mapping puis rejouez (Journal).' : '',
      (r.misrouted ?? 0) > 0 ? 'Mauvais magasin : fichier destiné à un autre magasin que son dossier — NON livré (§5.1).' : '',
      (r.duplicate ?? 0) > 0 ? 'Déjà intégré(s) : document(s) re-relevé(s) au même n° — NON réintégré(s) (§7bis).' : '',
      (r.unfetched ?? 0) > 0 ? 'NON relevé(s) : le fichier n\'a pas pu être lu (réseau, pièce introuvable). Il n\'est PAS acquitté et repassera à la prochaine relève — cause exacte au Journal.' : '',
    ].filter(Boolean).join('\n');
  };

  return (
    <Modal title={title} sub={sub} icon={icon} tone={tone} onClose={onClose}
      footer={
        phase === 'confirm'
          ? <><Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn><Btn mutating={false} icon={isFetch ? 'download' : 'play'} onClick={() => void run()}>{isFetch ? 'Relever maintenant' : 'Lancer le test'}</Btn></>
          : phase === 'running'
            ? <Btn variant="ghost" mutating={false} onClick={onClose}>Fermer</Btn>
            : <><Btn variant="ghost" mutating={false} onClick={onClose}>Fermer</Btn><Btn mutating={false} icon="check" onClick={onClose}>Terminé</Btn></>
      }>
      {phase === 'confirm' && (
        <p style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--ink-700)', lineHeight: 1.55 }}>
          {isFetch
            ? 'Les dossiers entrants sont relevés IMMÉDIATEMENT, sans attendre le créneau planifié : les fichiers présents sont récupérés, traduits et ajoutés au journal (puis archivés ou supprimés selon la configuration).'
            : 'Une connexion réelle est tentée avec les paramètres enregistrés. Aucun fichier n\'est récupéré ni déposé.'}
        </p>
      )}
      {phase === 'running' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
          <Spinner size={16} /><span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)' }}>{isFetch ? 'Relève en cours' : 'Connexion'} à {host || 'l\'hôte'}…</span>
        </div>
      )}
      {phase === 'done' && result && (
        <pre style={{
          margin: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: '10px 12px', borderRadius: 'var(--r-sm)',
          background: ok ? 'var(--ink-50)' : 'var(--rose-bg)', color: ok ? 'var(--ink-700)' : 'var(--rose-fg)', border: `1px solid ${ok ? 'var(--ink-200)' : 'var(--rose)'}`,
        }}>{ok
          ? (isFetch ? fetchSummary(result) : `${proto}${host ? ' ' + host : ''}\n${transport.type === 'email' ? 'Boîte IMAP accessible · serveur SMTP joignable.' : 'Authentification OK · dossier de dépôt/retrait accessible.'}`)
          : (result.error || 'Échec inconnu.')}</pre>
      )}
      {/* Le serveur a été refusé sur son identité : l'empreinte qu'il a présentée se confirme ici, après vérification
          auprès du fournisseur — aucun identifiant ne lui a été envoyé. Confirmée, le test est REJOUÉ : l'écran montre
          le vrai résultat de la connexion, pas une promesse. */}
      {phase === 'done' && result?.serverIdentity && (
        <div style={{ marginTop: 12 }}>
          <ServerIdentityBlock transportId={transport.id}
            identity={{ kind: transport.type === 'sftp' ? 'ssh' : 'tls', trusted: result.serverIdentity.trusted, offered: result.serverIdentity.offered, offeredAt: null }}
            onChanged={async () => { await onDone(); await run(); }} />
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// WIZARD — créer / modifier un fournisseur en 3 étapes (maquette § Fournisseurs) : Identité + Transport →
// Types de messages → Récapitulatif. Un seul flux couvre TOUS les éléments du fournisseur.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────

type WizStep = 1 | 2 | 3;
const STEP_LABELS: Record<WizStep, string> = { 1: 'Identité et transport', 2: 'Types de messages', 3: 'Récapitulatif' };

function SupplierWizard({ mode, supplier, onClose, onDone, showToast }: {
  mode: 'create' | 'edit';
  supplier: ApiSupplierDetail | null; // détail chargé en mode edit (identité + transports + mappings)
  onClose: () => void;
  onDone: (id?: string) => void | Promise<void>;
  showToast: Toast;
}) {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const { guard } = useApp(); // défense en profondeur : le handler de sauvegarde revérifie la capacité d'écriture
  const sup = supplier?.supplier;
  const existing = supplier?.transports ?? [];
  // Le serveur de fichiers d'un fournisseur se configure ici (un seul, valable pour tous ses clients). Par e-mail,
  // l'assistant ne déclare que l'adresse EDI et les expéditeurs : la boîte de chaque client se règle sur son lien.
  const fileTransports = existing.filter((t) => t.type !== 'email' && t.type !== 'as2');
  const primary = fileTransports.find((t) => t.active) ?? fileTransports[0] ?? null;
  // ⚠️ LE PROTOCOLE NE CHANGE PLUS DÈS QU'UN COMPTE EXISTE (le serveur le refuse aussi) : un fournisseur passé à
  //    l'e-mail garderait son serveur de fichiers, relevé sous un protocole que sa fiche ne montrerait plus.
  const protocolLocked = mode === 'edit' && existing.length > 0;

  const [step, setStep] = useState<WizStep>(1);
  const [name, setName] = useState(sup?.name ?? '');
  const nameRef = useRef<HTMLInputElement>(null);
  // ⚠️ Le nom prend le focus à chaque arrivée sur l'étape 1, et APRÈS la modale (l'effet d'un parent passe après celui
  //    de l'enfant) : un `autoFocus` serait pris pour le déclencheur, et le focus ne reviendrait plus au bouton.
  useEffect(() => { if (step === 1) nameRef.current?.focus(); }, [step]);
  const [gln, setGln] = useState(sup?.gln ?? '');
  const [types, setTypes] = useState<Set<string>>(new Set(sup?.messageTypes ?? []));
  // Transport : en édition avec un transport FICHIER existant, on ne saisit de nouveaux identifiants que si on
  // REMPLACE. Sans transport fichier (fournisseur email-only ou neuf), la saisie est ouverte d'emblée.
  const [editingTransport, setEditingTransport] = useState(mode === 'create' || !primary);
  const [proto, setProto] = useState<Proto>((sup?.protocol && sup.protocol !== 'as2' ? sup.protocol : (primary?.type as FileProto | undefined)) ?? 'sftp');
  const isFile = proto !== 'email';
  const [emailAddress, setEmailAddress] = useState(sup?.emailAddress ?? '');
  const [emailSenders, setEmailSenders] = useState((sup?.emailSenders ?? []).join('\n'));
  /** Une entrée par ligne (ou séparées par des virgules) ; le serveur valide chacune et nomme celle qui ne va pas. */
  const senders = emailSenders.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
  // FTPS : le mode TLS du compte, repris tel qu'enregistré — le perdre ferait repasser un compte implicite en explicite.
  const [tlsMode, setTlsMode] = useState<'explicit' | 'implicit'>(primary?.config?.tls ?? 'explicit');
  const [layoutMode, setLayoutMode] = useState<'shared-io' | 'per-client'>((primary?.config?.layout?.mode as 'per-client') ?? 'shared-io');
  const [f, setF] = useState({
    host: primary?.config?.host ?? '', port: primary?.config?.port ? String(primary.config.port) : '', user: primary?.config?.user ?? '',
    password: '', privateKey: '', passphrase: '', expectedFingerprint: '',
    inboundDir: primary?.config?.layout?.inboundDir ?? '/in', outboundDir: primary?.config?.layout?.outboundDir ?? '/out',
    rootDir: primary?.config?.layout?.rootDir ?? '/clients', archiveDir: primary?.config?.layout?.archiveDir ?? '',
  });
  const [busy, setBusy] = useState(false);
  const setField = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (k: string) => setTypes((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  // Un serveur de fichiers est à (ré)enregistrer si on est en saisie ET que l'hôte/utilisateur sont renseignés.
  const transportProvided = isFile && editingTransport && f.host.trim() !== '' && f.user.trim() !== '';

  const buildConfig = (proto: FileProto) => {
    const port = f.port ? Number(f.port) : undefined;
    const connection = proto === 'sftp'
      ? { protocol: proto, host: f.host.trim(), user: f.user.trim(), port, password: f.password || undefined, privateKey: f.privateKey.trim() || undefined, passphrase: f.passphrase || undefined }
      : { protocol: proto, host: f.host.trim(), user: f.user.trim(), port, password: f.password, ...(proto === 'ftps' ? { tls: tlsMode } : {}) };
    const archiveDir = f.archiveDir.trim() || undefined;
    const layout = layoutMode === 'shared-io'
      ? { mode: 'shared-io', inboundDir: f.inboundDir.trim(), outboundDir: f.outboundDir.trim(), archiveDir }
      : { mode: 'per-client', rootDir: f.rootDir.trim(), archiveDir };
    return { connection, layout };
  };

  // L'empreinte que le fournisseur a donnée, envoyée avec la configuration — seulement pour un protocole qui présente
  // une identité de serveur (le champ n'existe pas pour un FTP en clair).
  const expectedFingerprint = (proto === 'sftp' || proto === 'ftps') && f.expectedFingerprint.trim() ? { expectedFingerprint: f.expectedFingerprint.trim() } : {};

  // Ce qui manque à l'étape 1 pour enregistrer : le nom, et pour l'e-mail l'adresse EDI et un expéditeur — sans eux
  // rien ne partirait et tout mail reçu serait refusé (le serveur le refuse aussi, en le nommant).
  const missingStep1 = (): string[] => [
    [!name.trim(), 'le nom'],
    [proto === 'email' && !emailAddress.trim(), 'l’adresse EDI du fournisseur'],
    [proto === 'email' && senders.length === 0, 'un expéditeur autorisé'],
  ].filter(([manque]) => manque).map(([, label]) => label as string);

  // Enregistre l'ensemble : identité et protocole (POST/PATCH) puis, si de nouveaux identifiants sont fournis, le
  // serveur de fichiers. UN SEUL par fournisseur : mis à jour EN PLACE (PATCH) s'il existe, créé (POST) sinon.
  const save = async () => {
    const miss = missingStep1();
    if (!guard() || miss.length) { if (miss.length) { showToast('Requis : ' + miss.join(', '), 'warn'); setStep(1); } return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), gln: gln.trim(), messageTypes: [...types], protocol: proto, ...(proto === 'email' ? { email: { address: emailAddress.trim(), senders } } : {}) };
      let id = sup?.id;
      if (mode === 'create') { const r = await api.post<{ id: string }>('/admin/suppliers', payload); id = r.id; }
      else await api.patch(`/admin/suppliers/${id}`, payload);
      if (transportProvided && id) {
        if (primary) await api.patch(`/admin/transports/${primary.id}`, { config: buildConfig(proto), ...expectedFingerprint });
        else await api.post('/admin/transports', { supplierId: id, config: buildConfig(proto), ...expectedFingerprint });
      }
      showToast(mode === 'create' ? 'Fournisseur créé' : 'Fournisseur mis à jour', 'ok');
      await onDone(id);
    } catch (e) {
      showToast('Échec : ' + (e as Error).message, 'err');
      setBusy(false);
    }
  };

  // Avance d'une étape (le nom, et pour l'e-mail l'adresse et un expéditeur, sont requis dès l'étape 1).
  const goNext = () => {
    const miss = step === 1 ? missingStep1() : [];
    if (miss.length) { showToast('Requis : ' + miss.join(', '), 'warn'); return; }
    setStep((step + 1) as WizStep);
  };

  return (
    <Modal
      title={mode === 'create' ? 'Nouveau fournisseur' : `Modifier ${sup?.name ?? ''}`}
      sub={`Étape ${step}/3 · ${STEP_LABELS[step]}`}
      icon="building"
      onClose={onClose}
      maxWidth={800}
      footer={
        <>
          {step > 1
            ? <Btn variant="ghost" mutating={false} onClick={() => setStep((step - 1) as WizStep)}>← Retour</Btn>
            : <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>}
          <span style={{ flex: 1 }} />
          {step < 3 && <Btn variant={mode === 'edit' ? 'ghost' : 'primary'} mutating={false} onClick={goNext}>{step === 1 ? 'Types de messages →' : 'Récapitulatif →'}</Btn>}
          {(mode === 'edit' || step === 3) && (
            <Btn icon="check" onClick={() => void save()} disabled={busy || !name.trim()}>{busy ? 'Enregistrement…' : mode === 'create' ? 'Créer le fournisseur' : 'Enregistrer'}</Btn>
          )}
        </>
      }
    >
      {/* Barre de progression */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 16 }}>
        {([1, 2, 3] as WizStep[]).map((n) => (
          <div key={n} style={{ flex: 1, height: 4, borderRadius: 'var(--r-pill)', background: n <= step ? 'var(--teal-500)' : 'var(--ink-200)' }} />
        ))}
      </div>

      {/* ÉTAPE 1 — Identité + transport */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card style={{ padding: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
              <div><label htmlFor={`${fieldId}-0`} style={lbl}>Nom du fournisseur</label><input id={`${fieldId}-0`} ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} placeholder="Raison sociale" style={inp} /></div>
              <div><label htmlFor={`${fieldId}-1`} style={lbl}>GLN du fournisseur (optionnel)</label><input id={`${fieldId}-1`} value={gln} onChange={(e) => setGln(e.target.value)} placeholder="13 chiffres" style={{ ...inp, fontFamily: 'var(--font-mono)' }} /></div>
            </div>
          </Card>

          <div>
            <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-800)', marginBottom: 3 }}>Comment récupère-t-on ses fichiers ?</div>
            <p style={{ margin: '0 0 11px', fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.5 }}>Un seul protocole par fournisseur. Il vaudra pour tous les clients.</p>
            {protocolLocked && (
              <div role="status" style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginBottom: 10 }}>Ce fournisseur a déjà un transport enregistré : son protocole ne change plus.</div>
            )}
            <div style={{ marginBottom: 12 }}>
              <ProtoTiles value={proto} onPick={protocolLocked ? undefined : setProto} />
            </div>

            {proto === 'email' ? (
              <Card style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-700)' }}>Paramètres · E-mail <span style={{ fontWeight: 400, color: 'var(--ink-500)', fontSize: 'var(--t-meta)' }}>· valables pour tous les clients</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <div>
                    <label htmlFor={`${fieldId}-mail`} style={lbl}>Adresse EDI du fournisseur</label>
                    <input id={`${fieldId}-mail`} value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} placeholder="edi@fournisseur.com" style={{ ...inp, fontFamily: 'var(--font-mono)' }} />
                    <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 3 }}>Où partent nos envois : rapports de ventes, états de stock.</div>
                  </div>
                  <div>
                    <label htmlFor={`${fieldId}-senders`} style={lbl}>Expéditeurs autorisés</label>
                    <textarea id={`${fieldId}-senders`} value={emailSenders} onChange={(e) => setEmailSenders(e.target.value)} placeholder={'edi@fournisseur.com\n@fournisseur.com'} style={{ ...inp, height: 64, padding: 8, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }} />
                    <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 3 }}>Une adresse ou un domaine « @fournisseur.com » par ligne. Tout autre expéditeur est rejeté.</div>
                  </div>
                </div>
                <ClientSideNote proto="email" />
                <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>Chaque boîte se teste depuis la fiche, une fois déclarée sur le lien de son client.</div>
              </Card>
            ) : mode === 'edit' && primary && !editingTransport ? (
              // Transport existant : résumé masqué + remplacement à la demande (le secret n'est jamais réaffiché).
              <Card style={{ padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <Dot kind={primary.active ? 'ok' : 'off'} />
                  <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, textTransform: 'uppercase', fontFamily: 'var(--font-mono)', color: 'var(--ink-800)' }}>{primary.type}</span>
                  <span style={{ flex: 1 }} />
                  <Btn size="sm" variant="ghost" mutating={false} icon="refresh" onClick={() => setEditingTransport(true)}>Remplacer les identifiants</Btn>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  <ReadField label="Hôte" value={primary.config?.host ?? '—'} mono />
                  <ReadField label="Utilisateur" value={primary.config?.user ?? '—'} mono />
                  <ReadField label="Secret" value={primary.configIssue?.kind === 'no_secret' ? 'aucun' : '•••••••• · chiffré'} muted mono />
                </div>
                {primary.configIssue && <div role="status" style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 8 }}>{primary.configIssue.detail}</div>}
              </Card>
            ) : (
              <>
                <Card style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-700)' }}>Paramètres · {protoLabel(proto)} <span style={{ fontWeight: 400, color: 'var(--ink-500)', fontSize: 'var(--t-meta)' }}>· valables pour tous les clients</span></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                    <div><label htmlFor={`${fieldId}-2`} style={lbl}>Hôte</label><input id={`${fieldId}-2`} value={f.host} onChange={setField('host')} placeholder="edi.fournisseur.com" style={inp} /></div>
                    <div><label htmlFor={`${fieldId}-3`} style={lbl}>Port</label><input id={`${fieldId}-3`} value={f.port} onChange={setField('port')} placeholder={proto === 'sftp' ? '22' : proto === 'ftps' && tlsMode === 'implicit' ? '990' : '21'} style={inp} /></div>
                  </div>
                  {proto === 'ftps' && (
                    <div>
                      <span id={`${fieldId}-tls`} style={lbl}>Mode TLS</span>
                      <div role="group" aria-labelledby={`${fieldId}-tls`} style={{ display: 'flex', gap: 8 }}>
                        <LayoutTile on={tlsMode === 'explicit'} title="Explicite" desc="Connexion en clair puis AUTH TLS, port 21. Le plus courant." onClick={() => setTlsMode('explicit')} />
                        <LayoutTile on={tlsMode === 'implicit'} title="Implicite" desc="TLS dès la connexion, port 990." onClick={() => setTlsMode('implicit')} />
                      </div>
                    </div>
                  )}
                  <div><label htmlFor={`${fieldId}-4`} style={lbl}>Utilisateur</label><input id={`${fieldId}-4`} value={f.user} onChange={setField('user')} placeholder="orgazone" style={inp} /></div>
                  <div><label htmlFor={`${fieldId}-5`} style={lbl}>Mot de passe{proto === 'sftp' ? ' (ou clé privée ci-dessous)' : ''}</label><input id={`${fieldId}-5`} type="password" value={f.password} onChange={setField('password')} placeholder="•••••••• · stocké chiffré" style={inp} /></div>
                  {proto === 'sftp' && (
                    <>
                      <div><label htmlFor={`${fieldId}-6`} style={lbl}>Clé privée (PEM, optionnelle)</label><textarea id={`${fieldId}-6`} value={f.privateKey} onChange={setField('privateKey')} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" style={{ ...inp, height: 70, padding: 8, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }} /></div>
                      <div><label htmlFor={`${fieldId}-7`} style={lbl}>Passphrase de la clé (optionnelle)</label><input id={`${fieldId}-7`} type="password" value={f.passphrase} onChange={setField('passphrase')} style={inp} /></div>
                    </>
                  )}
                  {(proto === 'sftp' || proto === 'ftps') && (
                    <div>
                      <label htmlFor={`${fieldId}-fp`} style={lbl}>Empreinte du serveur (si le fournisseur vous l’a donnée)</label>
                      <input id={`${fieldId}-fp`} value={f.expectedFingerprint} onChange={setField('expectedFingerprint')} placeholder={proto === 'sftp' ? 'SHA256:…' : 'AB:CD:EF:… (64 chiffres hexadécimaux)'} style={{ ...inp, fontFamily: 'var(--font-mono)' }} />
                      <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 3 }}>
                        {proto === 'sftp'
                          ? 'Comparée au caractère près à celle du serveur : seul ce serveur sera accepté. Sans elle, le premier test proposera l’empreinte présentée, à confirmer.'
                          : 'Ne sert qu’à accepter un certificat qu’aucune autorité reconnue ne garantit (auto-signé) ; un certificat garanti est accepté sans elle.'}
                      </div>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid var(--ink-100)', paddingTop: 12 }}>
                    <span id={`${fieldId}-layout`} style={lbl}>Organisation des dossiers (§5.1)</span>
                    <div role="group" aria-labelledby={`${fieldId}-layout`} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <LayoutTile on={layoutMode === 'shared-io'} title="IN / OUT partagés" desc="Un dossier entrant + un sortant ; fichiers identifiés par le GLN du magasin." onClick={() => setLayoutMode('shared-io')} />
                      <LayoutTile on={layoutMode === 'per-client'} title="Dossier par client" desc="Une racine, un dossier par client (réglé côté Clients)." onClick={() => setLayoutMode('per-client')} />
                    </div>
                    {layoutMode === 'shared-io' ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        <div><label htmlFor={`${fieldId}-8`} style={lbl}>Dossier entrant</label><input id={`${fieldId}-8`} value={f.inboundDir} onChange={setField('inboundDir')} style={inp} /></div>
                        <div><label htmlFor={`${fieldId}-9`} style={lbl}>Dossier sortant</label><input id={`${fieldId}-9`} value={f.outboundDir} onChange={setField('outboundDir')} style={inp} /></div>
                        <div><label htmlFor={`${fieldId}-10`} style={lbl}>Archive (option)</label><input id={`${fieldId}-10`} value={f.archiveDir} onChange={setField('archiveDir')} placeholder="sinon supprimé" style={inp} /></div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                        <div><label htmlFor={`${fieldId}-11`} style={lbl}>Racine des dossiers clients</label><input id={`${fieldId}-11`} value={f.rootDir} onChange={setField('rootDir')} placeholder="/clients" style={inp} /></div>
                        <div><label htmlFor={`${fieldId}-12`} style={lbl}>Archive (option)</label><input id={`${fieldId}-12`} value={f.archiveDir} onChange={setField('archiveDir')} placeholder="sinon supprimé" style={inp} /></div>
                      </div>
                    )}
                  </div>
                  <ClientSideNote proto={proto} />
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                    <Icon name="info" size={14} /><span>La connexion se <b style={{ fontWeight: 600 }}>teste après enregistrement</b>, depuis la fiche (onglet Transports) — on ne peut sonder qu'un transport réellement enregistré (§23.0).</span>
                  </div>
                  {mode === 'edit' && primary && (
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>Un seul transport par fournisseur : ces identifiants <b>remplacent</b> la configuration actuelle (mise à jour en place) à l'enregistrement.</div>
                  )}
                </Card>
              </>
            )}
          </div>
        </div>
      )}

      {/* ÉTAPE 2 — Types de messages + statut de mapping (réel) */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-800)', marginBottom: 3 }}>Qu'est-ce que ce fournisseur sait échanger ?</div>
            <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.5 }}>Activez les types qu'il autorise. Un type activé <b>sans mapping</b> reste indisponible pour les clients tant que sa règle de traduction n'est pas importée (écran <LinkBtn onClick={() => { /* dans le wizard, l'import se fait après enregistrement via Mappings */ showToast('Importez le mapping depuis l\'écran Mappings après enregistrement', 'info'); }}>Mappings</LinkBtn>).</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
            {MSG_TYPES.map((t) => {
              const on = types.has(t.key);
              const mapped = (supplier?.mappings ?? []).some((m) => m.canonicalType === t.key);
              return (
                <button key={t.key} type="button" onClick={() => toggle(t.key)}
                  style={{ textAlign: 'left', padding: '12px 13px', borderRadius: 'var(--r-sm)', border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, background: on ? 'var(--teal-50)' : 'var(--paper)', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ width: 34, height: 20, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-300)', position: 'relative', flexShrink: 0, transition: 'background .12s' }}>
                      <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff', transition: 'left .12s' }} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>{t.key}</div>
                      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t.label}</div>
                    </div>
                  </div>
                  {on && (
                    <div style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: mapped ? 'var(--ok-fg)' : 'var(--rose-fg)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Icon name={mapped ? 'check' : 'alert'} size={12} sw={mapped ? 3 : 2} />{mapped ? 'Mapping actif' : 'Mapping manquant'}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ÉTAPE 3 — Récapitulatif */}
      {step === 3 && (
        <Card>
          <div style={{ padding: 16, borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-body)', fontWeight: 700, flexShrink: 0 }}>{initials(name || '—')}</div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{name || 'Sans nom'}</div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{gln.trim() ? `GLN ${gln.trim()}` : 'GLN non déclaré'} · {proto === 'email' ? `E-mail · ${emailAddress.trim() || 'adresse non déclarée'}` : `${protoLabel(proto)} ${transportProvided ? f.host.trim() : primary?.config?.host ?? '· serveur à configurer'}`}</div>
            </div>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 11 }}>
              <RecapStat n={types.size} label="types activés" tone="ink" />
              <RecapStat n={[...types].filter((k) => (supplier?.mappings ?? []).some((m) => m.canonicalType === k)).length} label="mappings prêts" tone="ok" />
              <RecapStat n={[...types].filter((k) => !(supplier?.mappings ?? []).some((m) => m.canonicalType === k)).length} label="mappings manquants" tone="rose" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[...types].length === 0 && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Aucun type activé — le fournisseur ne pourra rien échanger tant qu'un type n'est pas activé.</div>}
              {[...types].map((k) => {
                const mapped = (supplier?.mappings ?? []).some((m) => m.canonicalType === k);
                return (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                    <Dot kind={mapped ? 'ok' : 'warn'} />
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700, width: 130, flexShrink: 0, color: 'var(--ink-800)' }}>{k}</span>
                    <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', flex: 1, minWidth: 110 }}>{msgTypeLabel(k)}</span>
                    <span style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: mapped ? 'var(--ok-fg)' : 'var(--rose-fg)' }}>{mapped ? 'prêt' : 'à mapper'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
    </Modal>
  );
}

// Petite stat colorée du récapitulatif.
function RecapStat({ n, label, tone }: { n: number; label: string; tone: 'ink' | 'ok' | 'rose' }) {
  const bg = tone === 'ok' ? 'var(--ok-bg)' : tone === 'rose' ? 'var(--rose-bg)' : 'var(--ink-100)';
  const fg = tone === 'ok' ? 'var(--ok-fg)' : tone === 'rose' ? 'var(--rose-fg)' : 'var(--ink-600)';
  return (
    <div style={{ padding: '12px 13px', borderRadius: 'var(--r-sm)', background: bg, color: fg }}>
      <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{n}</div>
      <div style={{ fontSize: 'var(--t-dense)', lineHeight: 1.4 }}>{label}</div>
    </div>
  );
}

// Onglet de la fiche — habillage par la charte (`.tab`), y compris la barre d'activation.
function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={on ? 'tab active' : 'tab'}>
      {children}
    </button>
  );
}

// Lien texte inline (renvoi vers un autre écran).
function LinkBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--teal-600)', font: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>{children}</button>;
}

// Champ en LECTURE SEULE (hôte/user/secret) — le secret n'affiche qu'un masque (§13).
function ReadField({ label, value, mono, muted }: { label: string; value: string; mono?: boolean; muted?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 4 }}>{label}</div>
      <div style={{ height: 34, borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: muted ? 'var(--ink-50)' : 'var(--paper)', display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: 'var(--t-body)', fontFamily: mono ? 'var(--font-mono)' : 'inherit', color: muted ? 'var(--ink-500)' : 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  );
}

// Tuile de choix du layout (§5.1) — sélectionnée = bord teal.
function LayoutTile({ on, title, desc, onClick }: { on: boolean; title: string; desc: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--r-sm)', border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, background: on ? 'var(--teal-50)' : 'var(--paper)', boxShadow: on ? '0 0 0 3px var(--teal-100)' : 'none', cursor: 'pointer' }}>
      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{title}</div>
      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.4 }}>{desc}</div>
    </button>
  );
}

/** « Ce qui ne se règle pas ici » (maquette) : ce qui dépend du magasin et se saisit sur la fiche du client. */
function ClientSideNote({ proto }: { proto: Proto }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 13px', borderRadius: 'var(--r-ctrl)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
      <Icon name="info" size={14} />
      <span>
        <b style={{ fontWeight: 600 }}>Ce qui ne se règle pas ici :</b>{' '}
        {proto === 'email'
          ? 'la boîte de messagerie de chaque client — serveurs, identifiants, adresse d’expédition — se déclare sur son lien, une par magasin.'
          : 'le dossier de chaque client, quand les dossiers sont organisés par client (§5.1).'}{' '}
        Ces réglages dépendent du magasin, on les saisit sur la fiche du client.
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// LES BOÎTES DES CLIENTS — une par magasin et par fournisseur. Carte de lecture + modale (le formulaire est partagé
// avec la modale du lien, écran Clients). Le mapping reste au fournisseur ; seule la boîte diffère d'un client à l'autre.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────

// Carte d'une boîte client : client servi + boîte IMAP relevée + adresse d'expédition + actions.
function EmailAccountCard({ tr, clientName, onTest, onFetch, onEdit }: {
  tr: ApiSupplierDetail['transports'][number];
  clientName: string | null; // nom du client résolu via le GLN (null si non lié / non défini)
  onTest: () => void;
  onFetch: () => void;
  /** null : le compte ne sert aucun client lié, il n'a pas de lien où se modifier. */
  onEdit: (() => void) | null;
}) {
  const cfg = tr.config;
  const mailbox = cfg?.layout?.inboundDir || 'INBOX';
  const from = cfg?.smtp?.from || '—';
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <Dot kind={tr.active ? 'ok' : 'off'} />
        <span style={{ display: 'flex', color: 'var(--ink-500)' }}><Icon name="mail" size={14} /></span>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>{clientName ?? (tr.gln ? `GLN ${tr.gln}` : 'Client non défini')}</span>
        {tr.gln && clientName && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{tr.gln}</span>}
        {!tr.active && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>· inactif</span>}
        <span style={{ flex: 1 }} />
        <Btn size="sm" variant="ghost" mutating={false} icon="play" onClick={onTest}>Tester</Btn>
        <Btn size="sm" variant="ghost" mutating={false} icon="download" onClick={onFetch}>Relever</Btn>
        {onEdit && <Btn size="sm" variant="ghost" mutating={false} icon="cog" onClick={onEdit}>Modifier</Btn>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <ReadField label="Boîte relevée" value={`${cfg?.user ?? '—'} · ${cfg?.host ?? '—'} / ${mailbox}`} mono />
        <ReadField label="Adresse d’expédition" value={from} mono />
      </div>
      {tr.configIssue && <div role="status" style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{tr.configIssue.detail}</div>}
      {!tr.gln && (
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--rose-fg)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <Icon name="alert" size={12} />Aucun client affecté : ce compte ne sert aucun lien. Déclarez la boîte sur le lien du client concerné.
        </div>
      )}
    </div>
  );
}

/**
 * La boîte d'un client lié, déclarée ou remplacée depuis la fiche du fournisseur. Écrit par la même porte que la
 * modale du lien (`PUT /admin/links/:id/mailbox`) : le serveur y refuse une boîte déjà relevée ailleurs.
 */
function MailboxModal({ supplier, linkId, onClose, onDone, showToast }: {
  supplier: ApiSupplierDetail;
  /** Le lien dont on modifie la boîte ; null : choisir parmi les clients liés qui n'en ont pas. */
  linkId: string | null;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  showToast: Toast;
}) {
  const fieldId = useId();
  const { guard } = useApp(); // écriture réelle → gardée comme les autres mutations
  const accountOf = (gln: string) => supplier.transports.find((t) => t.type === 'email' && t.gln === gln) ?? null;
  const choices = linkId ? supplier.links.filter((l) => l.id === linkId) : supplier.links.filter((l) => !accountOf(l.gln));
  const [chosen, setChosen] = useState(choices[0]?.id ?? '');
  const link = supplier.links.find((l) => l.id === chosen) ?? null;
  const existing = link ? accountOf(link.gln)?.config ?? null : null;
  const [draft, setDraft] = useState(() => mailboxDraft(existing));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!guard() || !link) return;
    const miss = mailboxMissing(draft, existing);
    if (miss.length) { showToast('Champs requis : ' + miss.join(', '), 'warn'); return; }
    setBusy(true);
    try {
      await api.put(`/admin/links/${link.id}/mailbox`, { mailbox: mailboxInput(draft) });
      showToast(existing ? 'Boîte mise à jour' : 'Boîte déclarée', 'ok');
      await onDone();
    } catch (e) {
      showToast('Refusé : ' + (e as Error).message, 'err');
      setBusy(false);
    }
  };

  return (
    <Modal title={existing ? 'Modifier la boîte du client' : 'Déclarer la boîte d’un client'} sub={`${supplier.supplier.name} · e-mail · une boîte par magasin`} icon="mail" onClose={onClose} maxWidth={720}
      footer={<><Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn><span style={{ flex: 1 }} /><Btn icon="check" onClick={() => void save()} disabled={busy || !link}>{busy ? 'Enregistrement…' : 'Enregistrer la boîte'}</Btn></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label htmlFor={`${fieldId}-client`} style={lbl}>Client</label>
          {choices.length === 0 ? (
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Tous les clients liés ont déjà leur boîte.</div>
          ) : (
            <select id={`${fieldId}-client`} value={chosen} disabled={!!linkId} onChange={(e) => { setChosen(e.target.value); setDraft(mailboxDraft(null)); }} style={{ ...inp }}>
              {choices.map((l) => <option key={l.id} value={l.id}>{l.name ?? 'Site'} — {l.gln}</option>)}
            </select>
          )}
        </div>
        {link && <MailboxFields draft={draft} onChange={setDraft} existing={existing} supplierEmail={{ address: supplier.supplier.emailAddress, senders: supplier.supplier.emailSenders }} />}
      </div>
    </Modal>
  );
}
